//  in-flight.js
//  Transfers with only one leg posted yet.
//
//  A transfer between two linked accounts posts as two rows, but rarely on the
//  same day — the banks settle independently, so for a day or three exactly one
//  side exists. detectTransfers() sees a single unmatched row and, correctly by
//  its own rules, says nothing. That silence is wrong in one direction and
//  merely incomplete in the other:
//
//    card repayment posted, cash debit hasn't   -> CASH IS ABOUT TO DROP and the
//                                                  runway has no idea. This is a
//                                                  real near-term projection miss.
//    cash debit posted, card credit hasn't      -> cash is already correct; the
//                                                  card's owed balance reads high.
//
//  Only the first affects the cash ledger. Both are worth showing.
//
//  A third case hides behind the pairing itself: both legs exist, so
//  detectTransferPairs matches them and this module used to fall silent — but
//  the card's leg is still PENDING, and a pending credit is absent from the
//  ledger balance `owed` is read from. The pair is settled as far as matching
//  is concerned and unsettled as far as the balance is concerned. Case 0 below
//  covers it.
//
//  The whole feature turns on not crying wolf: any lone expense could in
//  principle be half of a transfer. So we only claim a leg is in flight when it
//  sits on a ROUTE WE'VE ALREADY SEEN COMPLETE — a bank->card pair the user has
//  actually made before. Without that gate every grocery run looks pending.

import { detectTransferPairs } from './transfers.js';
import { seriesKeyOf } from './series-key.js';

// How recent a lone leg has to be to count as "still settling". Past this it's
// not in flight, it's just a transfer whose other side we'll never see (paid
// from an account the user hasn't linked).
const DEFAULT_WINDOW_DAYS = 5;

// A route needs at least this many completed pairs before we'll infer a missing
// leg from it. One prior pair is a coincidence; two is a habit.
const MIN_ROUTE_PAIRS = 2;

function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(value) {
  const d = parseDate(value);
  return d ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : null;
}

function addDays(date, n) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + n);
  return d;
}

function median(values) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

const round2 = (n) => parseFloat(n.toFixed(2));

/**
 * Lone transfer legs whose counterpart hasn't posted yet.
 *
 * @param {Array} transactions  ALL transactions (cash and card)
 * @param {Array} accounts      accounts, needing `id`, `type`, `name`
 * @param {Object} [options]
 * @param {Date}   [options.today]
 * @param {number} [options.windowDays=5]
 * @param {Object} [options.pairs]  precomputed detectTransferPairs() result
 * @returns {Array<{
 *   id: string, amount: number, postedDate: Date, postedAccountId: string,
 *   missingAccountId: string, missingSide: 'cash'|'card',
 *   affects: 'cash'|'card', cardAccountId: string, expectedDate: Date,
 *   routeKey: string, description: string, cardName: string, delta: number,
 *   paired: boolean,
 * }>}
 *
 * `cardAccountId` is the card in question whichever leg posted, so callers
 * don't have to know which side went missing. `paired` says whether
 * detectTransferPairs already matched the two legs — false means the payment
 * is invisible to anything built from pairs (see buildPaymentRoutes, whose
 * coverage ratio would otherwise read the gap as a missed payment).
 */
export function detectInFlightTransfers(transactions, accounts, options = {}) {
  const {
    today = new Date(),
    windowDays = DEFAULT_WINDOW_DAYS,
    pairs: precomputed = null,
  } = options;

  const accountById = new Map((accounts || []).map((a) => [a.id, a]));
  const isCard = (id) => accountById.get(id)?.type === 'credit';

  const { ids, pairs } = precomputed || detectTransferPairs(transactions || []);
  const start = startOfDay(today) || new Date();
  const cutoff = addDays(start, -windowDays);

  // Learn the routes: bank -> card pairs the user has actually completed.
  const routes = new Map();
  pairs.forEach((p) => {
    if (!isCard(p.toAccountId)) return;
    const key = `${p.fromAccountId}->${p.toAccountId}`;
    if (!routes.has(key)) {
      routes.set(key, {
        key,
        cashAccountId: p.fromAccountId,
        cardAccountId: p.toAccountId,
        lags: [],
        count: 0,
        cashSeriesKeys: new Set(),
      });
    }
    const r = routes.get(key);
    r.count++;
    r.lags.push(p.lagDays);
    r.cashSeriesKeys.add(seriesKeyOf(p.outflow));
  });

  const established = [...routes.values()].filter((r) => r.count >= MIN_ROUTE_PAIRS);

  const routesByCard = new Map();
  const routesByCash = new Map();
  established.forEach((r) => {
    if (!routesByCard.has(r.cardAccountId)) routesByCard.set(r.cardAccountId, []);
    routesByCard.get(r.cardAccountId).push(r);
    if (!routesByCash.has(r.cashAccountId)) routesByCash.set(r.cashAccountId, []);
    routesByCash.get(r.cashAccountId).push(r);
  });

  const out = [];

  // Case 0 — BOTH legs found each other, but the card's leg is still pending.
  //
  // Pairing and settlement are independent facts, and conflating them is what
  // made this case invisible. The pair proves the transfer is real; `pending`
  // on the card's leg says the issuer hasn't absorbed it yet. Plaid's credit
  // `current` — the ledger `owed` is read from — excludes pending credits,
  // while a depository `available` has already netted the debit out. So the
  // card reports the full amount owed against a cash balance that has already
  // dropped: precisely the double-count the unpaired cases below exist to
  // prevent, arriving through the one door that was left open.
  //
  // No route gate here. MIN_ROUTE_PAIRS guards against mistaking a lone
  // grocery run for half a transfer; a matched pair is the evidence itself.
  pairs.forEach((p) => {
    if (!isCard(p.toAccountId)) return;
    if (!p.inflow?.pending) return;
    const amount = Math.abs(Number(p.amount) || 0);
    if (amount === 0) return;
    const posted = startOfDay(p.outflowDate);
    if (!posted) return;
    out.push({
      id: p.inflowId,
      amount: round2(amount),
      postedDate: posted,
      postedAccountId: p.fromAccountId,
      missingAccountId: p.toAccountId,
      missingSide: 'card',
      affects: 'card',
      cardAccountId: p.toAccountId,
      expectedDate: posted,
      routeKey: `${p.fromAccountId}->${p.toAccountId}`,
      description: p.outflow?.description || 'Transfer',
      cardName: accountById.get(p.toAccountId)?.name || 'Card',
      delta: round2(-amount),
      paired: true,
    });
  });

  if (established.length === 0) return out.sort((a, b) => b.postedDate - a.postedDate);

  (transactions || []).forEach((t) => {
    if (!t || t.id == null || !t.accountId) return;
    if (ids.has(t.id)) return; // already matched to its other leg
    const posted = startOfDay(t.date);
    if (!posted || posted > start) return;
    // The recency window is a guess about how long settlement takes, and it
    // exists to stop a permanently-unmatched leg from reading as "still
    // settling" forever. A row the bank still flags PENDING needs no guess:
    // the money has left the available balance and has not landed, and the
    // bank is saying so directly. It stays in flight for as long as that
    // holds — no cap, because the sync deletes pending rows the moment the
    // bank stops reporting them, so a stale one cannot linger here.
    if (!t.pending && posted < cutoff) return;
    const amount = Math.abs(Number(t.amount) || 0);
    if (amount === 0) return;

    // Case 1 — the cash-affecting one. A repayment landed on a card that this
    // user pays from a known account, and that account hasn't been debited yet.
    if (t.type === 'repayment' && isCard(t.accountId)) {
      const route = (routesByCard.get(t.accountId) || [])[0];
      if (!route) return;
      const lag = median(route.lags);
      // lagDays is (inflow - outflow), so the cash side normally leads by `lag`.
      // That points into the past here, which is the whole problem — the debit
      // is overdue, so expect it immediately.
      const expected = startOfDay(addDays(posted, -Math.round(lag)));
      out.push({
        id: t.id,
        amount: round2(amount),
        postedDate: posted,
        postedAccountId: t.accountId,
        missingAccountId: route.cashAccountId,
        missingSide: 'cash',
        affects: 'cash',
        cardAccountId: t.accountId,
        expectedDate: expected > start ? expected : start,
        routeKey: route.key,
        description: `${accountById.get(t.accountId)?.name || 'Card'} payment`,
        cardName: accountById.get(t.accountId)?.name || 'Card',
        delta: round2(-amount),
        paired: false,
      });
      return;
    }

    // Case 2 — cash already left, the card hasn't been credited yet. No effect
    // on the runway; the card just reads as owing more than it really does.
    if (t.type === 'expense' && !isCard(t.accountId)) {
      const candidates = routesByCash.get(t.accountId) || [];
      const route = candidates.find((r) => r.cashSeriesKeys.has(seriesKeyOf(t)));
      if (!route) return;
      out.push({
        id: t.id,
        amount: round2(amount),
        postedDate: posted,
        postedAccountId: t.accountId,
        missingAccountId: route.cardAccountId,
        missingSide: 'card',
        affects: 'card',
        cardAccountId: route.cardAccountId,
        expectedDate: posted,
        routeKey: route.key,
        description: t.description || 'Transfer',
        cardName: accountById.get(route.cardAccountId)?.name || 'Card',
        delta: round2(-amount),
        paired: false,
      });
    }
  });

  return out.sort((a, b) => b.postedDate - a.postedDate);
}

/**
 * The subset that belongs in the cash ledger, shaped like a runway event.
 */
export function inFlightCashEvents(inFlight) {
  return (inFlight || [])
    .filter((f) => f.affects === 'cash')
    .map((f) => ({
      date: f.expectedDate,
      seriesKey: `inflight__${f.id}`,
      description: f.description,
      type: 'expense',
      amount: f.amount,
      delta: f.delta,
      inFlight: true,
      isCardPayment: true,
    }));
}

export const _internal = { MIN_ROUTE_PAIRS, DEFAULT_WINDOW_DAYS, median };
